import { supabase } from '../../shared/supabase';
import { processFoodItems } from '../../shared/utils/imageOptimization';
import { MENU_CATEGORY_DEFINITIONS } from '../../shared/utils/homepageCategoryUtils';

export const categories = MENU_CATEGORY_DEFINITIONS;

/**
 * Constants for tag variations
 */
export const BESTSELLER_TAGS = [
  'bestseller',
  'best seller',
  'best-seller',
  'best_seller',
  'top seller',
  'popular',
  'featured',
  'trending',
  'hot',
];

export const RECOMMENDED_TAGS = [
  'recommended',
  'recommend',
  'suggested',
  'trending',
  'hot',
  'new',
  'must try',
  'top pick',
  'chef choice',
  "chef's choice",
];

/**
 * Fetches all food items for a restaurant with image fallback handling.
 */
export const fetchFoodItems = async (restaurantId) => {
  if (!restaurantId) return [];
  try {
    const { data, error } = await supabase
      .from('fooditems')
      .select('*')
      .eq('restaurant_id', restaurantId)
      // Use sort_order (admin drag order) when set; items with no sort_order fall back to newest-first
      .order('sort_order', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Process items to ensure they have valid images
    const processedItems = processFoodItems(data || []);
    return processedItems;
  } catch (error) {
    return [];
  }
};

/**
 * Helper function to check if an item has a specific tag (case-insensitive)
 */
export const hasTag = (item, targetTags) => {
  if (!item.tags || !Array.isArray(item.tags)) return false;

  // Convert target tags to lowercase for comparison
  const targetTagsLower = targetTags.map((tag) => tag.toLowerCase());

  // Check if any of the item's tags match any of the target tags (case-insensitive)
  return item.tags.some((itemTag) => {
    const itemTagLower = itemTag.toLowerCase();
    return targetTagsLower.includes(itemTagLower);
  });
};

/**
 * OPTIMIZED: Fetches only items tagged as 'bestseller' with case-insensitive filtering and image fallback handling.
 */
export const fetchBestSellerItems = async (restaurantId) => {
  if (!restaurantId) return [];
  try {
    // First, fetch all items for the restaurant
    const { data, error } = await supabase
      .from('fooditems')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Filter items with case-insensitive tag matching
    const bestSellerItems = (data || []).filter((item) => {
      return hasTag(item, BESTSELLER_TAGS);
    });

    // Process items to ensure they have valid images
    const processedItems = processFoodItems(bestSellerItems);
    return processedItems;
  } catch (error) {
    return [];
  }
};

/**
 * NEW & OPTIMIZED: Fetches only items tagged as 'recommended' with case-insensitive filtering and image fallback handling.
 */
export const fetchRecommendedItems = async (restaurantId) => {
  if (!restaurantId) return [];
  try {
    // First, fetch all items for the restaurant
    const { data, error } = await supabase
      .from('fooditems')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Filter items with case-insensitive tag matching
    const recommendedItems = (data || []).filter((item) => {
      return hasTag(item, RECOMMENDED_TAGS);
    });

    // Process items to ensure they have valid images
    const processedItems = processFoodItems(recommendedItems);
    return processedItems;
  } catch (error) {
    return [];
  }
};

/**
 * Fetches carousel items from Supabase with restaurant filter
 */
export const fetchMaincaroselData = async (restaurantId = null) => {
  try {
    // Note: carousel offers may not have is_active set — don't filter on it here
    let query = supabase.from('fooditems').select('*').eq('is_carousel', true);

    // Filter by restaurant if restaurantId is provided
    if (restaurantId) {
      query = query.eq('restaurant_id', restaurantId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      return [];
    }

    // Transform the data to match the expected format
    return data.map((item) => ({
      id: item.id,
      restaurant_id: item.restaurant_id, // <-- add this!
      imade: item.image, // Note: keeping 'imade' to match existing component
      discount: formatDiscount(item),
      message: formatMessage(item),
      name: item.name,
      price: item.price,
      discountedPrice: item.discounted_price,
      category: item.category,
      tags: item.tags || [],
      // restaurant_id is missing here!
    }));
  } catch (error) {
    return [];
  }
};

const CAROUSEL_MESSAGE_MAX = 52;

const truncateCarouselText = (text, max = CAROUSEL_MESSAGE_MAX) => {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  const cut = trimmed.slice(0, max).trimEnd();
  const lastSpace = cut.lastIndexOf(' ');
  const safe = lastSpace > 24 ? cut.slice(0, lastSpace).trimEnd() : cut;
  return `${safe}…`;
};

// Helper function to format carousel headline text (kept short for the slider)
const formatMessage = (item) => {
  if (item.description && item.description.trim().length > 0) {
    return truncateCarouselText(item.description);
  }

  if (item.category) {
    return truncateCarouselText(`Delicious ${item.category} Special!`);
  }

  return truncateCarouselText(item.name || 'Special Offer!');
};

// Helper function to format discount display
const formatDiscount = (item) => {
  // Check if item has discount information
  if (!item.discounttype || !item.discountvalue) {
    // No discount information available, create a dynamic message

    if (item.price && item.discounted_price && item.price > item.discounted_price) {
      const savings = item.price - item.discounted_price;
      const savingsPercent = Math.round((savings / item.price) * 100);

      if (savingsPercent >= 10) {
        return `${savingsPercent}% OFF`;
      }

      // Otherwise show the absolute savings
      return `SAVE ₹${savings.toFixed(0)}`;
    }

    // Check for tags to create a personalized message
    if (item.tags && item.tags.length > 0) {
      // Look for specific tags that might indicate a special offer
      if (
        item.tags.some((tag) =>
          ['new', 'featured', 'bestseller', 'popular', 'trending', 'hot'].includes(
            tag.toLowerCase(),
          ),
        )
      ) {
        const specialTag = item.tags.find((tag) =>
          ['new', 'featured', 'bestseller', 'popular', 'trending', 'hot'].includes(
            tag.toLowerCase(),
          ),
        );

        return specialTag.toUpperCase();
      }
    }

    // Create enticing offers based on category or random selection
    const specialOffers = [
      'HOT DEAL!',
      "TODAY'S SPECIAL",
      'MUST TRY!',
      'TOP PICK',
      "CHEF'S CHOICE",
    ];

    // Use category if available to personalize the offer
    if (item.category) {
      if (item.category.toLowerCase().includes('dessert')) {
        return 'SWEET TREAT';
      } else if (item.category.toLowerCase().includes('breakfast')) {
        return 'MORNING SPECIAL';
      } else if (item.category.toLowerCase().includes('dinner')) {
        return 'DINNER DELIGHT';
      } else if (item.category.toLowerCase().includes('lunch')) {
        return 'LUNCH SPECIAL';
      }
    }

    const randomIndex = Math.floor(Math.random() * specialOffers.length);
    return specialOffers[randomIndex];
  }

  // Use the provided discount information if available
  switch (item.discounttype.toLowerCase()) {
    case 'percentage':
      return `${item.discountvalue}% OFF`;
    case 'fixed':
      return `₹${item.discountvalue} OFF`;
    case 'buy_one_get_one':
    case 'bogo':
      return 'BUY 1 GET 1';
    case 'buy_x_get_y':
      return `BUY ${item.discountvalue} GET 1`;
    default:
      return `${item.discountvalue}% OFF`;
  }
};

// Fallback static data (in case of network issues or empty database)
export const fallbackMaincaroselData = [
  {
    id: 'fallback-1',
    imade: null,
    discount: '30% OFF',
    message: 'Experience our delicious new dish',
  },
  {
    id: 'fallback-2',
    imade: null,
    discount: 'BUY 1 GET 1',
    message: 'Try our spicy treat today',
  },
  {
    id: 'fallback-3',
    imade: null,
    discount: "Chef's Choice",
    message: 'Tastier than ever before!',
  },
];
